import React from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'
import { Formik } from 'formik'
import * as Yup from 'yup'

import saveSeats from 'core/application/adminDashboard/managePeople/viewPeople/saveSeats'
import getSeatsForSave, { SeatsForSave } from 'core/application/adminDashboard/managePeople/viewPeople/seatsForSave'

import FormikDefaultInputField from '../../../../../common/components/dataEntry/formik/FormikDefaultInputField'
import Alert from '../../../../../common/components/feedback/Alert'
import Modal from '../../../../../common/components/feedback/Modal'
import Icon from '../../../../../common/components/general/Icon'
import { formatDataAsDashed } from '../../../../../common/dateFormatters'
import useCommandRequest from '../../../../../common/hooks/useCommandRequest'
import useQueryRequest from '../../../../../common/hooks/useQueryRequest'

interface EditModalProps {
  visible: boolean
  onCancel?: () => void
  onSaved?: () => void
  planPeriodId: string
}

const EditSeatModal = (props: EditModalProps) => {
  const { t: translate } = useTranslation()
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const [initialValues] = useQueryRequest<SeatsForSave>(null, () => getSeatsForSave(props.planPeriodId))
  const onSave = async (values: SeatsForSave) => {
    const result = await sendCommand(() =>
      saveSeats({
        seatNumber: values.totalSeats,
        planPeriodId: props.planPeriodId,
      }),
    )

    if (result.completed && props.onSaved) {
      props.onSaved()
    }
  }

  return (
    <Formik
      enableReinitialize={true}
      validationSchema={formValidator(translate, initialValues?.minimumAllowedSeats)}
      initialValues={initialValues}
      onSubmit={() => {}}
      validateOnChange={true}
    >
      {(formik) => {
        return (
          <form autoComplete="off">
            <Modal
              okButtonProps={{ disabled: !formik.isValid || isCommandLoading, loading: isCommandLoading }}
              visible={props.visible}
              onOk={() => onSave(formik.values)}
              okText={'Update seats'}
              closable
              onCancel={props.onCancel}
              title={translate('admin-dashboard.seats.header')}
            >
              <Skeleton loading={!initialValues}>
                {initialValues && (
                  <>
                    <Row>
                      <Col span={24} className={'mt-8'}>
                        <h5>{translate('admin-dashboard.seats.main-text')}</h5>
                      </Col>
                      <Col span={24}>
                        <h6>{translate('admin-dashboard.seats.secondary-text')}</h6>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Row>
                          <Col span={24} className={'mt-24'}>
                            <h6>{translate('admin-dashboard.seats.label')}</h6>
                          </Col>
                        </Row>
                        <FormikDefaultInputField
                          placeholder={`${initialValues.minimumAllowedSeats}`}
                          name={'totalSeats'}
                          autoFocus={true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        {formik?.values?.totalSeats < initialValues?.minimumAllowedSeats && (
                          <p className="color-red-6">
                            {translate('admin-dashboard.seats.error-message', {
                              minimum: initialValues?.minimumAllowedSeats,
                            })}
                          </p>
                        )}
                      </Col>
                      <Col span={24} className={'mt-24'}>
                        {formik?.values?.totalSeats === initialValues?.totalSeats && (
                          <Alert
                            showIcon
                            icon={<Icon className={'fal fa-info-circle'} />}
                            message={translate('admin-dashboard.seats.default-alert')}
                            type={'info'}
                          />
                        )}
                        {formik?.values?.totalSeats > initialValues?.totalSeats && (
                          <Alert
                            showIcon
                            icon={<Icon className={'fal fa-exclamation-triangle'} />}
                            message={translate('admin-dashboard.seats.increased-number-alert')}
                            type={'warning'}
                          />
                        )}
                        {formik?.values?.totalSeats < initialValues?.totalSeats &&
                          formik?.values?.totalSeats >= initialValues?.minimumAllowedSeats && (
                            <Alert
                              showIcon
                              icon={<Icon className={'fal fa-exclamation-triangle'} />}
                              message={translate('admin-dashboard.seats.decreased-number-alert', {
                                date: formatDataAsDashed(initialValues.nextBillingDate),
                              })}
                              type={'warning'}
                            />
                          )}
                      </Col>
                    </Row>
                  </>
                )}
              </Skeleton>
            </Modal>
          </form>
        )
      }}
    </Formik>
  )
}

const formValidator = (translate: any, minimumAllowedSeats: number) =>
  Yup.object({
    totalSeats: Yup.number().min(minimumAllowedSeats, ' ').typeError('').required(translate('general.required')),
  })

export default EditSeatModal
